/**
 * Cryptographic authentication utilities for DFitClub frontend
 */

// API Key from environment variable
const API_KEY = process.env.REACT_APP_API_KEY;
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Debug: Log crypto auth configuration
if (typeof window !== 'undefined') {
  console.log('🔐 Crypto Auth Configuration:');
  console.log('  API_KEY configured:', !!API_KEY);
  console.log('  API_BASE_URL:', API_BASE_URL);
}

// Token cache to reduce API calls
let tokenCache = {
  token: null,
  expiresAt: 0
};

// Token cache duration (4 minutes to ensure we refresh before 5-minute expiry)
const TOKEN_CACHE_DURATION = 4 * 60 * 1000; // 4 minutes in milliseconds

/**
 * Generate encrypted token from backend
 * @returns {Promise<string>} Encrypted token
 */
export const generateEncryptedToken = async () => {
  try {
    // Check if we have a valid cached token
    const now = Date.now();
    if (tokenCache.token && now < tokenCache.expiresAt) {
      return tokenCache.token;
    }
    const response = await fetch(`${API_BASE_URL}/auth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });

    if (!response.ok) {
      throw new Error(`Token generation failed: ${response.status}`);
    }

    const data = await response.json();
    
    // Cache the token
    tokenCache.token = data.encrypted_token;
    tokenCache.expiresAt = now + TOKEN_CACHE_DURATION;
    
    return data.encrypted_token;
  } catch (error) {
    console.error('Failed to generate encrypted token:', error);
    throw error;
  }
};

/**
 * Get authentication headers with encrypted token
 * @returns {Promise<Object>} Headers object with encrypted token
 */
export const getEncryptedAuthHeaders = async () => {
  const headers = {
    'Content-Type': 'application/json',
  };

  try {
    // Generate encrypted token
    const encryptedToken = await generateEncryptedToken();
    headers['X-Encrypted-Token'] = encryptedToken;
    
    return headers;
  } catch (error) {
    console.error('Failed to get encrypted auth headers:', error);
    // Don't fallback to regular API key - fail instead
    throw new Error('Encrypted authentication required but failed');
  }
};

/**
 * Create authenticated axios instance with encrypted tokens
 * @param {Object} axios - Axios instance
 * @returns {Object} Configured axios instance with encrypted auth
 */
export const createEncryptedAxios = (axios) => {
  // Add request interceptor to include encrypted token
  axios.interceptors.request.use(
    async (config) => {
      try {
        // Skip token generation for token endpoint to avoid infinite loop
        if (config.url === '/auth/token') {
          if (API_KEY) {
            config.headers['X-API-Key'] = API_KEY;
          }
          return config;
        }

        // Always try to generate encrypted token first
        const encryptedToken = await generateEncryptedToken();
        config.headers['X-Encrypted-Token'] = encryptedToken;
        
        return config;
      } catch (error) {
        console.error('Encrypted authentication failed:', error);
        // Only fallback to regular API key for non-critical endpoints
        if (API_KEY && (config.url.includes('/health') || config.url.includes('/config'))) {
          config.headers['X-API-Key'] = API_KEY;
        } else {
          // For critical endpoints, fail if encrypted auth doesn't work
          throw new Error('Encrypted authentication failed');
        }
        return config;
      }
    },
    (error) => {
      console.error('Request interceptor error:', error);
      return Promise.reject(error);
    }
  );

  // Add response interceptor to handle auth errors
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error.response?.status === 401) {
        console.error('Encrypted authentication failed. Token may be expired.');
      } else if (error.response?.status === 429) {
        console.error('Rate limit exceeded. Please try again later.');
      }
      return Promise.reject(error);
    }
  );

  return axios;
};

/**
 * Test encrypted authentication
 * @param {Object} axios - Axios instance
 * @returns {Promise<boolean>} True if authentication works
 */
export const testEncryptedAuthentication = async (axios) => {
  try {
    const response = await axios.get('/api/health');
    return response.status === 200;
  } catch (error) {
    console.error('Encrypted authentication test failed:', error);
    return false;
  }
};

/**
 * Get authentication status
 * @returns {Object} Authentication status information
 */
export const getEncryptedAuthStatus = () => {
  return {
    apiKeyConfigured: !!API_KEY,
    apiKeyLength: API_KEY ? API_KEY.length : 0,
    apiKeyPrefix: API_KEY ? API_KEY.substring(0, 10) + '...' : 'Not set',
    encryptionEnabled: true
  };
};